
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { auth, db } from './config';
import { awardXP, isTaskCompleted } from './gamification';
import { getUserLearningStats } from './courses';
import { learningPlansService } from './learningPlans';
import { 
  UserProgress, 
  QuizAttempt, 
  QuizAnswer,
  ProgressStatus
} from '../../types/course';

// ...existing code...
export class ProgressService {
  /**
   * Get the current active learning session for a user, course, and module
   */
  async getActiveLearningSession(courseId: string, moduleId: string): Promise<string | null> {
    if (!auth.currentUser) return null;
    const userId = auth.currentUser.uid;
    try {
      const q = query(
        collection(db, 'learning_sessions'),
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('moduleId', '==', moduleId),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        // Return the first active session's ID
        return snapshot.docs[0].id;
      }
      return null;
    } catch (error) {
      console.error('[getActiveLearningSession] Error:', { courseId, moduleId, error });
      return null;
    }
  }
  /**
   * Update module progress when user completes or accesses a module
   */

  async updateModuleProgress(
    courseId: string,
    moduleId: string,
    timeSpent: number,
    completed: boolean = false
  ): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const userId = auth.currentUser.uid;
      const docId = `${userId}_${courseId}`;
      const progressRef = doc(db, 'user_progress', docId);
      const progressDoc = await getDoc(progressRef);
      if (progressDoc.exists()) {
        const progressData = progressDoc.data() as UserProgress;
        // Update or add module progress
        const moduleIndex = progressData.moduleProgress.findIndex(mp => mp.moduleId === moduleId);
        if (moduleIndex >= 0) {
          // Update existing module progress
          const updatedModuleProgress: any = {
            ...progressData.moduleProgress[moduleIndex],
            timeSpent: progressData.moduleProgress[moduleIndex].timeSpent + timeSpent,
            status: completed ? ProgressStatus.COMPLETED : ProgressStatus.IN_PROGRESS
          };
          if (completed) {
            updatedModuleProgress.completedAt = Timestamp.now();
          } else if (progressData.moduleProgress[moduleIndex].completedAt) {
            updatedModuleProgress.completedAt = progressData.moduleProgress[moduleIndex].completedAt;
          }
          progressData.moduleProgress[moduleIndex] = updatedModuleProgress;
        } else {
          // Add new module progress
          const newModuleProgress: any = {
            moduleId,
            timeSpent,
            status: completed ? ProgressStatus.COMPLETED : ProgressStatus.IN_PROGRESS
          };
          if (completed) {
            newModuleProgress.completedAt = Timestamp.now();
          }
          progressData.moduleProgress.push(newModuleProgress);
        }
        // Calculate completion stats
        // Fetch the actual modules to get the real count (don't trust course.totalModules)
        const modulesQuery = query(
          collection(db, 'modules'),
          where('courseId', '==', courseId)
        );
        const modulesSnapshot = await getDocs(modulesQuery);
        const actualModuleCount = modulesSnapshot.size;
        
        // Use actual module count if available, otherwise fall back to progress data
        const totalModules = actualModuleCount > 0 ? actualModuleCount : progressData.totalModules;
        
        // Update course document if totalModules is incorrect (only if user has permission)
        if (actualModuleCount > 0) {
          try {
            const courseRef = doc(db, 'courses', courseId);
            const courseSnap = await getDoc(courseRef);
            if (courseSnap.exists() && courseSnap.data().totalModules !== actualModuleCount) {
              await updateDoc(courseRef, { totalModules: actualModuleCount });
            }
          } catch (courseUpdateError) {
            // Silently fail if user doesn't have permission - this is a nice-to-have fix
            // No logging needed as this is expected for non-admin users
          }
        }
        const completedCount = progressData.moduleProgress.filter(mp => mp.status === ProgressStatus.COMPLETED).length;
        let completionPercentage = totalModules > 0 ? (completedCount / totalModules) * 100 : 0;
        if (completionPercentage > 100) completionPercentage = 100;
        // Update progress document
        const updatePayload: any = {
          moduleProgress: progressData.moduleProgress,
          completedModules: completedCount,
          totalModules,
          completionPercentage,
          totalTimeSpent: progressData.totalTimeSpent + timeSpent,
          updatedAt: Timestamp.now()
        };
        if (completionPercentage === 100) {
          updatePayload.completedAt = Timestamp.now();

          // Award XP for course completion milestones
          try {
            await this.checkAndAwardCourseCompletionXP(userId, courseId);
          } catch (xpError) {
            console.error('[Gamification] Error awarding course completion XP:', xpError);
          }
        } else if (progressData.completedAt) {
          // Only try to remove completedAt if it exists
          updatePayload.completedAt = deleteField();
        }
        await updateDoc(progressRef, updatePayload);

        // Notify the dashboard to refresh Today's Goals whenever a module is completed
        if (completed) {
          window.dispatchEvent(new CustomEvent('gamificationUpdate'));
        }
      } else {
        // Create new progress document
        const newModuleProgress: any = {
          moduleId,
          timeSpent,
          status: completed ? ProgressStatus.COMPLETED : ProgressStatus.IN_PROGRESS
        };
        if (completed) {
          newModuleProgress.completedAt = Timestamp.now();
        }
        const newProgress: Omit<UserProgress, 'id'> = {
          userId,
          courseId,
          status: completed ? ProgressStatus.COMPLETED : ProgressStatus.IN_PROGRESS,
          enrolledAt: Timestamp.now(),
          lastAccessedAt: Timestamp.now(),
          moduleProgress: [newModuleProgress],
          completedModules: completed ? 1 : 0,
          totalModules: 1, // This should be updated when we know the actual total
          completionPercentage: completed ? 100 : 0,
          totalTimeSpent: timeSpent,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        await setDoc(progressRef, newProgress).catch((err) => {
          console.error('setDoc user_progress error:', err, newProgress);
        });

        // Notify the dashboard to refresh Today's Goals when a new completed module is recorded
        if (completed) {
          window.dispatchEvent(new CustomEvent('gamificationUpdate'));
        }
      }

      try {
        await learningPlansService.calculateDashboardStats();
      } catch (statsError) {
        console.error('Error recalculating dashboard stats after progress update:', statsError);
      }
    } catch (error) {
      console.error('updateModuleProgress Firestore error:', error);
      throw error;
    }
  }

  /**
   * Record a quiz attempt
   */
  async recordQuizAttempt(
    courseId: string,
    moduleId: string,
    answers: { [questionId: string]: number },
    score: number,
    totalQuestions: number,
    timeSpent: number
  ): Promise<void> {
    if (!auth.currentUser) return;

    try {
      // Convert answers to QuizAnswer format
      const quizAnswers: QuizAnswer[] = Object.entries(answers).map(([questionId, userAnswer]) => ({
        questionId,
        userAnswer,
        isCorrect: true, // This would need to be calculated based on correct answers
        pointsEarned: 1 // This would need to be calculated
      }));


      const userId = auth.currentUser.uid;
      const quizAttempt: Omit<QuizAttempt, 'id'> = {
        userId,
        courseId,
        moduleId,
        answers: quizAnswers,
        score,
        timeSpent,
        passed: (score / totalQuestions) >= 0.7, // 70% pass rate
        attemptNumber: await this.getNextAttemptNumber(courseId, moduleId),
        startedAt: Timestamp.now(),
        completedAt: Timestamp.now(),
      };

      await addDoc(collection(db, 'quiz_attempts'), quizAttempt).catch((err) => { console.error('addDoc quiz_attempts error:', err, quizAttempt); });
      
      // Update module progress if quiz passed
      if (quizAttempt.passed) {
        await this.updateModuleProgress(courseId, moduleId, timeSpent, true);
      }
    } catch (error) {
    }
  }

  /**
   * Get next attempt number for a quiz
   */
  private async getNextAttemptNumber(courseId: string, moduleId: string): Promise<number> {
    if (!auth.currentUser) return 1;

    try {
      const userId = auth.currentUser.uid;
      const attemptsQuery = query(
        collection(db, 'quiz_attempts'),
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('moduleId', '==', moduleId),
        orderBy('attemptNumber', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(attemptsQuery);
      if (snapshot.empty) {
        return 1;
      }

      const lastAttempt = snapshot.docs[0].data();
      return (lastAttempt.attemptNumber || 0) + 1;
    } catch (error) {
      return 1;
    }
  }

  /**
   * Get user progress for a course
   */
  async getUserProgress(courseId: string): Promise<UserProgress | null> {
    if (!auth.currentUser) return null;

    try {
      const userId = auth.currentUser.uid;
      const docId = `${userId}_${courseId}`;
      const progressRef = doc(db, 'user_progress', docId);
      const progressDoc = await getDoc(progressRef);
      if (progressDoc.exists()) {
        const data = { id: progressDoc.id, ...progressDoc.data() } as UserProgress;
        
        // Recalculate completion percentage based on actual module count
        const modulesQuery = query(
          collection(db, 'modules'),
          where('courseId', '==', courseId)
        );
        const modulesSnapshot = await getDocs(modulesQuery);
        const actualModuleCount = modulesSnapshot.size;
        
        if (actualModuleCount > 0) {
          const completedCount = data.moduleProgress?.filter(mp => mp.status === ProgressStatus.COMPLETED).length || 0;
          const correctTotalModules = actualModuleCount;
          let correctCompletionPercentage = (completedCount / correctTotalModules) * 100;
          if (correctCompletionPercentage > 100) correctCompletionPercentage = 100;
          
          // If the stored values are incorrect, update them
          if (data.totalModules !== correctTotalModules || Math.abs(data.completionPercentage - correctCompletionPercentage) > 0.1) {
            const updatePayload: any = {
              totalModules: correctTotalModules,
              completionPercentage: correctCompletionPercentage,
              completedModules: completedCount,
              updatedAt: Timestamp.now()
            };
            
            // Mark as completed if 100%
            if (correctCompletionPercentage === 100 && !data.completedAt) {
              updatePayload.completedAt = Timestamp.now();
              
              // Award XP for course completion
              await this.checkAndAwardCourseCompletionXP(userId, courseId);
            }
            
            // Update the progress document
            await updateDoc(progressRef, updatePayload);
            
            // Update the returned data
            data.totalModules = correctTotalModules;
            data.completionPercentage = correctCompletionPercentage;
            data.completedModules = completedCount;
            if (updatePayload.completedAt) {
              data.completedAt = updatePayload.completedAt;
            }
            
            // Also update the course document if needed
            const courseRef = doc(db, 'courses', courseId);
            const courseSnap = await getDoc(courseRef);
            if (courseSnap.exists() && courseSnap.data().totalModules !== correctTotalModules) {
              await updateDoc(courseRef, { totalModules: correctTotalModules });
            }
          }
        }
        
        return data;
      }

      return null;
    } catch (error) {
      console.error('[getUserProgress] Error:', error);
      return null;
    }
  }

  /**
   * Get quiz attempts for a module
   */
  async getQuizAttempts(courseId: string, moduleId: string): Promise<QuizAttempt[]> {
    if (!auth.currentUser) return [];

    try {
      const userId = auth.currentUser.uid;
      const attemptsQuery = query(
        collection(db, 'quiz_attempts'),
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('moduleId', '==', moduleId),
        orderBy('attemptNumber', 'desc')
      );

      const attemptsSnapshot = await getDocs(attemptsQuery);
      const attempts: QuizAttempt[] = [];

      attemptsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        attempts.push({
          id: doc.id,
          ...data,
          startedAt: data.startedAt?.toDate() || Timestamp.now(),
          completedAt: data.completedAt?.toDate() || Timestamp.now(),
        } as unknown as QuizAttempt);
      });

      return attempts;
    } catch (error) {
      return [];
    }
  }

  /**
   * Start a learning session
   */
  async startLearningSession(courseId: string, moduleId: string): Promise<string> {
    if (!auth.currentUser) return '';

    try {
      // Check for existing active session first
      const existingSessionId = await this.getActiveLearningSession(courseId, moduleId);
      if (existingSessionId) {
        return existingSessionId;
      }

      const userId = auth.currentUser.uid;
      const session: any = {
        userId,
        courseId,
        moduleId,
        startedAt: Timestamp.now(),
        isActive: true
      };

      const docRef = await addDoc(collection(db, 'learning_sessions'), session);
      return docRef.id;
    } catch (error) {
      console.error('[startLearningSession] Error starting session:', { courseId, moduleId, error });
      return '';
    }
  }

  /**
   * End a learning session
   */
  async endLearningSession(sessionId: string): Promise<void> {
    if (!sessionId) return;

    try {
      const sessionRef = doc(db, 'learning_sessions', sessionId);
      const payload = {
        endedAt: Timestamp.now(),
        isActive: false
      };
      const sessionDoc = await getDoc(sessionRef);
      if (!sessionDoc.exists()) {
        console.error('endLearningSession: session does not exist', { sessionId });
        return;
      }
      await updateDoc(sessionRef, payload);
    } catch (error) {
      console.error('endLearningSession Firestore error:', { sessionId, error });
    }
  }

  /**
   * Get all module completion dates for the current user
   */
  async getModuleCompletionDates(): Promise<Timestamp[]> {
    if (!auth.currentUser) return [];

    try {
      const userId = auth.currentUser.uid;
      const progressQuery = query(
        collection(db, 'user_progress'),
        where('userId', '==', userId)
      );
      const progressSnapshot = await getDocs(progressQuery);
      const completionDates: Timestamp[] = [];

      progressSnapshot.docs.forEach(doc => {
        const data = doc.data() as UserProgress;
        data.moduleProgress.forEach(module => {
          if (module.status === ProgressStatus.COMPLETED && module.completedAt) {
            completionDates.push(module.completedAt);
          }
        });
      });

      return completionDates;
    } catch (error) {
      console.error('Error fetching module completion dates:', error);
      return [];
    }
  }

  /**
   * Get user learning statistics for the current month
   */
  async getCurrentMonthLearningStats(): Promise<any | null> {
    if (!auth.currentUser) return null;

    try {
      const userId = auth.currentUser.uid;
      
      // Get start and end of current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      
      // Get all learning sessions for current month
      const sessionsQuery = query(
        collection(db, 'learning_sessions'),
        where('userId', '==', userId),
        where('startedAt', '>=', Timestamp.fromDate(startOfMonth)),
        where('startedAt', '<=', Timestamp.fromDate(endOfMonth))
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      let monthlyTimeSpent = 0;
      
      sessionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.startedAt && data.endedAt) {
          const start = data.startedAt.toDate();
          const end = data.endedAt.toDate();
          const duration = (end.getTime() - start.getTime()) / 1000; // Convert to seconds
          monthlyTimeSpent += duration;
        }
      });

      return {
        monthlyTimeSpent
      };
    } catch (error) {
      console.error('Error fetching current month stats:', error);
      return null;
    }
  }

  /**
   * Get user learning statistics (all time)
   */
  async getUserLearningStats(): Promise<any | null> {
    if (!auth.currentUser) return null;

    try {
      const userId = auth.currentUser.uid;
      
      // Get all user progress
      const progressQuery = query(
        collection(db, 'user_progress'),
        where('userId', '==', userId)
      );
      const progressSnapshot = await getDocs(progressQuery);
      
      let totalCoursesEnrolled = 0;
      let totalCoursesCompleted = 0;
      let totalTimeSpent = 0;
      let totalModulesCompleted = 0;

      progressSnapshot.docs.forEach(doc => {
        const data = doc.data();
        totalCoursesEnrolled++;
        totalTimeSpent += data.totalTimeSpent || 0;
        totalModulesCompleted += data.completedModules || 0;
        
        if (data.completionPercentage === 100) {
          totalCoursesCompleted++;
        }
      });

      // Get quiz attempts for average score
      const quizQuery = query(
        collection(db, 'quiz_attempts'),
        where('userId', '==', userId)
      );
      const quizSnapshot = await getDocs(quizQuery);
      
      let totalQuizScore = 0;
      let totalQuizAttempts = 0;

      quizSnapshot.docs.forEach(doc => {
        const data = doc.data();
        totalQuizScore += data.score || 0;
        totalQuizAttempts++;
      });

      const averageQuizScore = totalQuizAttempts > 0 ? totalQuizScore / totalQuizAttempts : 0;

      return {
        coursesEnrolled: totalCoursesEnrolled,
        coursesCompleted: totalCoursesCompleted,
        totalTimeSpent,
        averageQuizScore,
        currentStreak: 0, // Would need to calculate based on daily activity
        longestStreak: 0, // Would need to calculate based on historical data
        totalModulesCompleted,
        achievements: [] // Would need to calculate based on milestones
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Check and award XP for course completion milestones
   */
  private async checkAndAwardCourseCompletionXP(userId: string, completedCourseId: string): Promise<void> {
    try {
      // Get user's learning stats to count completed courses
      const stats = await getUserLearningStats(userId);
      const totalCompleted = stats.coursesCompleted;

      // Check if the completed course is free or paid
      const courseDoc = await getDoc(doc(db, 'courses', completedCourseId));
      const courseData = courseDoc.data();
      const isFree = courseData?.price === 0 || courseData?.isFree === true;

      // Award XP for first free course
      if (isFree) {
        const alreadyCompleted = await isTaskCompleted(userId, 'complete_first_free_course');
        if (!alreadyCompleted) {
          await awardXP(userId, 'complete_first_free_course', 50);
          // eslint-disable-next-line no-console
          console.log('[Gamification] Awarded 50 XP for completing first free course');
          window.dispatchEvent(new CustomEvent('gamificationUpdate'));
        }
      } else {
        // Award XP for first paid course
        const alreadyCompletedFirst = await isTaskCompleted(userId, 'complete_first_paid_course');
        if (!alreadyCompletedFirst) {
          await awardXP(userId, 'complete_first_paid_course', 20);
          // eslint-disable-next-line no-console
          console.log('[Gamification] Awarded 20 XP for completing first paid course');
          window.dispatchEvent(new CustomEvent('gamificationUpdate'));
        }

        // Count paid courses completed
        const progressQuery = query(
          collection(db, 'user_progress'),
          where('userId', '==', userId),
          where('completionPercentage', '==', 100)
        );
        const progressSnapshot = await getDocs(progressQuery);
        let paidCoursesCompleted = 0;

        for (const progressDoc of progressSnapshot.docs) {
          const progressData = progressDoc.data();
          const courseDocRef = await getDoc(doc(db, 'courses', progressData.courseId));
          const courseInfo = courseDocRef.data();
          if (courseInfo && courseInfo.price > 0 && !courseInfo.isFree) {
            paidCoursesCompleted++;
          }
        }

        // Award XP for 5 paid courses
        if (paidCoursesCompleted >= 5) {
          const alreadyCompleted5 = await isTaskCompleted(userId, 'complete_5_paid_courses');
          if (!alreadyCompleted5) {
            await awardXP(userId, 'complete_5_paid_courses', 100);
            // eslint-disable-next-line no-console
            console.log('[Gamification] Awarded 100 XP for completing 5 paid courses');
            window.dispatchEvent(new CustomEvent('gamificationUpdate'));
          }
        }
      }

      // Award XP for 10 total courses
      if (totalCompleted >= 10) {
        const alreadyCompleted10 = await isTaskCompleted(userId, 'finish_10_courses');
        if (!alreadyCompleted10) {
          await awardXP(userId, 'finish_10_courses', 100);
          // eslint-disable-next-line no-console
          console.log('[Gamification] Awarded 100 XP for finishing 10 courses');
          window.dispatchEvent(new CustomEvent('gamificationUpdate'));
        }
      }
    } catch (error) {
      console.error('[checkAndAwardCourseCompletionXP] Error:', error);
    }
  }
}
export const progressService = new ProgressService();;

